import React, { useState, useEffect, useRef } from "react";
// UI Components
import { Menu, MenuItem, CircularProgress, Divider } from "@material-ui/core";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import GetAppIcon from "@material-ui/icons/GetApp";
// Custom Components
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_CircularIndeternment from "../../../../Atoms/Feedback/ProgressIndicators/A_CircularIndeternment";
// Utilities & Helpers
import { getPPtData } from "./M_ToolbarFilterhelper";
// State Management (Redux)
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as previousUploadActions from "../../../../../../store/actions/previousUploadActions";
// Routing
import { useParams } from "react-router";
// PropTypes
import PropTypes from "prop-types";

const PollingDownloadPptButton = (props) => {
    const { config, handlePptDownload } = props;
    const presentation = config?.presentation ?? [];
    const [anchorEl, setAnchorEl] = useState(null);
    const [mainButtonLoading, setMainButtonLoading] = useState(false);
    const [downloading, setDownloading] = useState({});
    const [generatedDates, setGeneratedDates] = useState({});
    const prevProcessingState = useRef({});
    let { crmId, pageUrl } = useParams();
    const intervalRef = useRef(null);
    const timeoutRef = useRef(null);
    const fetchControllerRef = useRef(null);

    const extractIds = (presentation) => Array.isArray(presentation) ? presentation.map(item => item?.id).filter(Boolean) : [];

    useEffect(() => {
        fetchData();
        intervalRef.current = setInterval(fetchData, 5000);
        return () => clearInterval(intervalRef.current);
    }, []);

    const fetchData = async () => {
        let ids = extractIds(presentation);
        if (ids.length > 0) {
            if (fetchControllerRef.current) {
                fetchControllerRef.current.abort();
            }
            fetchControllerRef.current = new AbortController();
            const { signal } = fetchControllerRef.current;
            try {
                let response = await getPPtData(ids, { signal });
                if (!response || typeof response !== "object") return;
                const updatedStatus = {};
                const updatedDates = {};
                let anyDownloading = false;
                let shouldReload = false;
                ids.forEach((id) => {
                    const prevProcessing = prevProcessingState.current[id];
                    const currentProcessing = response[id]?.isProcessing ?? false;
                    if (prevProcessing && !currentProcessing) {
                        shouldReload = true;
                    }
                    if (currentProcessing) {
                        updatedStatus[id] = "downloading";
                        anyDownloading = true;
                    } else {
                        updatedStatus[id] = "";
                    }
                    if (response[id]?.lastGeneratedDate) {
                        updatedDates[id] = formatDate(response[id]?.lastGeneratedDate);
                    }
                });

                setDownloading((prev) => ({ ...prev, ...updatedStatus }));
                setGeneratedDates((prev) => ({ ...prev, ...updatedDates }));
                setMainButtonLoading(anyDownloading);
                prevProcessingState.current = { ...updatedStatus };
                if (shouldReload) {
                    props.getPreviousUpload({
                        crmId: crmId,
                        pageUrl: pageUrl,
                        container: config?.reloadCard?.containerType,
                        directory: config?.reloadCard?.directory,
                    });
                }
            } catch (error) {
                if (error.name === "AbortError") {
                    // eslint-disable-next-line no-console
                    console.log("Fetch request was aborted.");
                } else {
                    // eslint-disable-next-line no-console
                    console.error("Error fetching PPT data:", error);
                }
            }
        }
    };

    const handleDownload = (item) => {
        handlePptDownload(config, item);

        if (item?.id) {
            setDownloading((prev) => ({ ...prev, [item.id]: "downloading" }));
            setMainButtonLoading(true);
        }

        // Cancel the ongoing fetch request if it exists
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
        }

        // Stop polling and restart after 7 seconds
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(fetchData, 5000);
        }, 7000);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };


    const formatDate = (dateString) => {
        if (!dateString) return "Not available";
        const date = new Date(dateString);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    };

    return (
        <div style={{ marginRight: "8px" }}>
            <A_Button
                edge="start"
                color="primary"
                aria-label="add records"
                label={config?.label ?? "Download"}
                onClick={handleClick}
                endIcon={
                    mainButtonLoading ? (
                        <A_CircularIndeternment color="secondary" size={20} />
                    ) : (
                        <GetAppIcon />
                    )
                }
            />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
                {Array.isArray(presentation) && presentation.length > 0 ? (
                    presentation.map((item, index) => (
                        <div key={item?.id ?? index}>
                            <MenuItem
                                onClick={() => handleDownload(item)}
                                disabled={downloading[item?.id] === "downloading"}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    minWidth: "150px",
                                    width: "auto",
                                    padding: "10px 15px",
                                    gap: "20px",
                                }}
                            >
                                {/* Text Content */}
                                <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                                    <span style={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
                                        {item?.label ?? "Unknown"}
                                    </span>
                                    {config?.showGeneratedDate && (
                                        <span style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                                            Last generated: {generatedDates[item?.id] || "Not available"}
                                        </span>
                                    )}
                                </div>

                                {/* Status Icon */}
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {downloading[item?.id] === "downloading" ? (
                                        <CircularProgress size={20} style={{ marginRight: "5px" }} />
                                    ) : (
                                        <CloudDownloadIcon style={{ color: "#007bff" }} />
                                    )}
                                </div>
                            </MenuItem>
                            {index < presentation.length - 1 && <Divider />}
                        </div>
                    ))
                ) : (
                    <MenuItem disabled style={{ padding: "10px 20px", color: "#999" }}>
                        No presentations available
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
};

PollingDownloadPptButton.propTypes = {
    config: PropTypes.shape({
        label: PropTypes.string,
        presentation: PropTypes.array,
        showGeneratedDate: PropTypes.bool,
        reloadCard: PropTypes.any
    }),
    handlePptDownload: PropTypes.func,
    getPreviousUpload: PropTypes.func,
};

function mapStateToProps(state) {
    return {
        previousUpload: state.previousUpload
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        Object.assign({}, previousUploadActions),
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(PollingDownloadPptButton);
